type Attrs = Record<string, string | number | boolean | EventListener | undefined | null>;
type Child = Node | string | number | null | undefined | false;

/** Tiny DOM builder: h('div.cls#id', { onclick }, ...children). */
export const h = <K extends keyof HTMLElementTagNameMap>(sel: K | `${K}.${string}` | `${K}#${string}`, attrs: Attrs = {}, ...children: Child[]): HTMLElementTagNameMap[K] => {
  const [tagAndId, ...classes] = sel.split('.');
  const [tag, id] = (tagAndId as string).split('#');
  const el = document.createElement(tag as K);
  if (id) el.id = id;
  if (classes.length) el.className = classes.join(' ');
  for (const [k, v] of Object.entries(attrs)) {
    if (v === undefined || v === null || v === false) continue;
    if (k.startsWith('on') && typeof v === 'function') el.addEventListener(k.slice(2), v as EventListener);
    else if (k === 'html') el.innerHTML = String(v);
    else if (v === true) el.setAttribute(k, '');
    else el.setAttribute(k, String(v));
  }
  for (const c of children) {
    if (c === null || c === undefined || c === false) continue;
    el.append(typeof c === 'string' || typeof c === 'number' ? document.createTextNode(String(c)) : c);
  }
  return el;
};

export const svg = (markup: string, cls = ''): HTMLElement => {
  const span = document.createElement('span');
  span.className = cls;
  span.style.display = 'inline-grid';
  span.innerHTML = markup;
  return span;
};

export const clear = (el: Element): void => {
  while (el.firstChild) el.removeChild(el.firstChild);
};

/** Sets the text only when it changes: the HUD redraws every tick, and an equal write still replaces the text node. */
export const setText = (el: Node, text: string): void => {
  if (el.textContent !== text) el.textContent = text;
};

/**
 * The window's size, kept from its resize events. Read every frame by what follows the board on screen: reading
 * `window.innerWidth` right after the page was written to makes the browser lay it out again on the spot, and with
 * each tag placed in turn that was most of a phone's frame outside three (2026-10-31).
 */
let vw = typeof window === 'undefined' ? 0 : window.innerWidth;
let vh = typeof window === 'undefined' ? 0 : window.innerHeight;
if (typeof window !== 'undefined')
  window.addEventListener('resize', () => {
    vw = window.innerWidth;
    vh = window.innerHeight;
  });
export const viewW = (): number => vw;
export const viewH = (): number => vh;

/** What each element's style was last set to here (the browser reads a property back in its own form). */
const STYLED = new WeakMap<HTMLElement, Map<string, string>>();

/**
 * Sets a style property only when it changes: what follows a board piece on screen is placed every frame, and an
 * equal write still makes the page work its styles out again.
 */
export const setStyle = (el: HTMLElement, prop: string, value: string): void => {
  let m = STYLED.get(el);
  if (!m) STYLED.set(el, (m = new Map()));
  if (m.get(prop) === value) return;
  m.set(prop, value);
  el.style.setProperty(prop, value);
};

/** Bar fills already turned to be drawn by scale (see setFill). */
const FILLS = new WeakSet<HTMLElement>();

/**
 * A bar's fill at `k` (0..1), drawn by its scale across instead of its width: the same picture (the fills shade
 * top to bottom), but an eased change of width laid the page out and repainted it every frame it ran, and in a
 * fight the bars change nearly every tick. Its own transition is kept, moved to the scale.
 */
export const setFill = (el: HTMLElement, k: number): void => {
  if (!FILLS.has(el)) {
    FILLS.add(el);
    const cs = getComputedStyle(el);
    el.style.width = '100%';
    el.style.transformOrigin = '0 50%';
    if (cs.transitionProperty.includes('width')) el.style.transition = `transform ${cs.transitionDuration} ${cs.transitionTimingFunction}`;
  }
  setStyle(el, 'transform', `scaleX(${Math.min(1, Math.max(0, k)).toFixed(4)})`);
};

/** Shows or hides only when that changes. */
export const setHidden = (el: HTMLElement, hidden: boolean): void => {
  if (el.hidden !== hidden) el.hidden = hidden;
};
